/**
 * A photo for something not owned yet.
 *
 * Brian, 12 Aug 2026, on the To buy tab: "it needs to have images. And I'm happy to generate
 * those if you prompt me for them."
 *
 * Deliberately not the same component as PhotoField. That one edits a garment inside a form and
 * carries a preview, a Replace button and a destructive warning. This sits in the empty photo
 * square of a card, has to work at 96px, and only ever handles the first upload — once a want
 * has a picture the card renders the picture instead and this is gone. Sharing one component
 * would have meant a `compact` branch through every line of it.
 *
 * Limits still mirror worker/routes/wardrobe.ts exactly: jpeg/png/webp, 8MB.
 */
import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { WardrobeGap } from '../../shared/types';
import { cx, errorText } from './ui';

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 8 * 1024 * 1024;

export default function GapPhotoField({
  gap,
  onUploaded,
  compact = false,
}: {
  gap: WardrobeGap;
  onUploaded: (key: string) => void;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function send(file: File) {
    setError(null);
    if (!ACCEPTED.includes(file.type)) {
      setError('JPEG, PNG or WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Under 8MB.');
      return;
    }
    const form = new FormData();
    form.append('photo', file);
    setBusy(true);
    try {
      const r = await api.upload<{ image_r2_key: string }>(
        `/wardrobe/gaps/${gap.id}/photo`,
        form
      );
      onUploaded(r.image_r2_key);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
      // Same reason as PhotoField: without this, re-picking the same filename fires nothing.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <span
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) void send(f);
      }}
      className={cx(
        'flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-center transition-colors',
        dragging && 'bg-sage-soft'
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void send(f);
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={cx(
          'rounded px-1 text-[11px] leading-tight font-medium text-ink-soft underline',
          'underline-offset-2 hover:text-ink disabled:opacity-50',
          'focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none'
        )}
      >
        {busy ? 'Uploading…' : error ? 'Try again' : compact ? 'Add photo' : 'Add a photo'}
      </button>
      {error && <span className="px-1 text-[10px] leading-tight text-clay">{error}</span>}
    </span>
  );
}
